/**
 * AdminUsersPage - Gestion complète des utilisateurs
 * 
 * Permet aux administrateurs de :
 * - Créer, modifier, supprimer des utilisateurs
 * - Gérer les quotas d'agents actifs
 * - Régénérer les clés API
 * - Se connecter en tant qu'utilisateur (impersonation)
 * - Voir les statistiques par utilisateur
 * 
 * Conforme WCAG 2.1 AA et charte graphique TotemAI
 */
import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { LogIn, Loader2, Users, ArrowLeft, Shield, Plus, Edit, Trash2, Key, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { getApiBase, getToken } from '../lib/utils';
import { api } from '../lib/api';

type User = {
  id: number;
  email: string;
  is_superuser: boolean;
  first_name?: string | null;
  last_name?: string | null;
  api_key?: string | null;
  max_active_agents?: number | null;
  agents_count?: number;
  subjects_count?: number;
  conversations_count?: number;
  created_at?: string;
};

type UserWithApiKey = User & { api_key?: string | null };

type ModalProps = {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
};

function Modal({ open, onClose, children }: ModalProps) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden />
      <div className="relative z-10 w-full max-w-lg bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg shadow-xl p-6">
        {children}
      </div>
    </div>
  );
}

interface ImpersonationResponse {
  access_token: string;
  token_type: string;
  user: {
    id: number;
    email: string;
  };
  impersonation_log_id: number;
  expires_at: string;
}

export default function AdminUsersPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [impersonatingUserId, setImpersonatingUserId] = useState<number | null>(null);
  const [users, setUsers] = useState<User[] | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', password: '', role: 'client' });
  const [formError, setFormError] = useState<string | null>(null);
  const [formSuccess, setFormSuccess] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [tableMessage, setTableMessage] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [editForm, setEditForm] = useState({ firstName: '', lastName: '', email: '', password: '', role: 'client', maxActiveAgents: '' });
  const [editError, setEditError] = useState<string | null>(null);
  const [editSuccess, setEditSuccess] = useState<string | null>(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [rotatingId, setRotatingId] = useState<number | null>(null);
  const [apiKeyModal, setApiKeyModal] = useState<{ email: string; apiKey: string } | null>(null);
  const [apiKeyCopyState, setApiKeyCopyState] = useState<'idle' | 'copied' | 'error'>('idle');

  const generateStrongPassword = () => {
    const length = 16;
    const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const lowercase = 'abcdefghijklmnopqrstuvwxyz';
    const numbers = '0123456789';
    const symbols = '!@#$%^&*()_+-=[]{}|;:,.<>?';
    const allChars = uppercase + lowercase + numbers + symbols;
    
    let password = '';
    // Garantir au moins un caractère de chaque type
    password += uppercase[Math.floor(Math.random() * uppercase.length)];
    password += lowercase[Math.floor(Math.random() * lowercase.length)];
    password += numbers[Math.floor(Math.random() * numbers.length)];
    password += symbols[Math.floor(Math.random() * symbols.length)];
    
    // Compléter avec des caractères aléatoires
    for (let i = password.length; i < length; i++) {
      password += allChars[Math.floor(Math.random() * allChars.length)];
    }
    
    // Mélanger le mot de passe
    return password.split('').sort(() => Math.random() - 0.5).join('');
  };

  const handleGeneratePassword = () => {
    const newPassword = generateStrongPassword();
    setForm(prev => ({ ...prev, password: newPassword }));
    toast.success('Mot de passe fort généré');
  };

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    setTableMessage(null);
    try {
      const res = await api.get<User[]>('/fapi/users/admin/all');
      setUsers(res.data);
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.message || 'Erreur chargement utilisateurs';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const regenerateApiKey = async (user: User) => {
    setTableMessage(null);
    setRotatingId(user.id);
    try {
      const { data } = await api.post<UserWithApiKey>(`/fapi/users/${user.id}/api-key`);
      if (data.api_key) {
        setApiKeyModal({ email: data.email, apiKey: data.api_key });
      }
      setUsers(prev => prev ? prev.map(u => (u.id === data.id ? { ...u, api_key: null } : u)) : prev);
      toast.success('Nouvelle clé API générée.');
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.message || 'Erreur régénération clé API';
      toast.error(msg);
    } finally {
      setRotatingId(null);
    }
  };

  useEffect(() => {
    if (apiKeyModal) {
      setApiKeyCopyState('idle');
    }
  }, [apiKeyModal]);

  const handleCopyApiKey = async () => {
    if (!apiKeyModal) return;
    try {
      await navigator.clipboard.writeText(apiKeyModal.apiKey);
      setApiKeyCopyState('copied');
      setTimeout(() => setApiKeyCopyState('idle'), 2500);
    } catch {
      setApiKeyCopyState('error');
      setTimeout(() => setApiKeyCopyState('idle'), 2500);
    }
  };

  const deleteUser = async (id: number) => {
    if (!users?.find(u => u.id === id)) return;
    if (!confirm('Supprimer cet utilisateur et toutes les données associées ?')) return;
    setTableMessage(null);
    try {
      setDeletingId(id);
      const { data } = await api.delete<{ status: string; deleted: { user_id: number; media: number; subjects: number } }>(`/fapi/users/${id}`);
      setUsers(prev => prev ? prev.filter(u => u.id !== id) : prev);
      toast.success(`Utilisateur supprimé. Médias: ${data?.deleted?.media ?? 0}, sujets: ${data?.deleted?.subjects ?? 0}.`);
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.message || 'Erreur suppression utilisateur';
      toast.error(msg);
    } finally {
      setDeletingId(null);
    }
  };

  const openEditModal = (user: User) => {
    setEditingUser(user);
    setEditError(null);
    setEditSuccess(null);
    setEditForm({
      firstName: user.first_name ?? '',
      lastName: user.last_name ?? '',
      email: user.email,
      password: '',
      role: user.is_superuser ? 'admin' : 'client',
      maxActiveAgents: user.max_active_agents !== null ? String(user.max_active_agents) : '',
    });
    setShowEditModal(true);
  };

  const submitEditUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingUser) return;
    setEditError(null);
    setEditSuccess(null);
    if (!editForm.email.trim()) {
      setEditError('Email requis');
      return;
    }

    const payload: any = {
      email: editForm.email.trim(),
      first_name: editForm.firstName.trim() || null,
      last_name: editForm.lastName.trim() || null,
      is_superuser: editForm.role === 'admin',
    };
    if (editForm.password.trim()) {
      payload.password = editForm.password;
    }

    setSavingEdit(true);
    try {
      const { data } = await api.patch<User>(`/fapi/users/${editingUser.id}`, payload);
      const maxActiveAgents = editForm.maxActiveAgents.trim() ? parseInt(editForm.maxActiveAgents) : null;
      
      const quotaUrl = maxActiveAgents !== null 
        ? `/agents/admin/users/${editingUser.id}/active-agents-quota?max_active_agents=${maxActiveAgents}`
        : `/agents/admin/users/${editingUser.id}/active-agents-quota`;
      
      await api.patch(quotaUrl);
      await fetchUsers();
      setEditSuccess('Utilisateur mis à jour');
      toast.success('Utilisateur mis à jour');
      setEditingUser({ ...data, max_active_agents: maxActiveAgents });
      setEditForm(prev => ({ ...prev, password: '' }));
    } catch (err: any) {
      console.error('Error updating user:', err);
      let msg = 'Erreur mise à jour utilisateur';
      if (err?.response?.data?.detail) {
        if (typeof err.response.data.detail === 'string') {
          msg = err.response.data.detail;
        } else if (Array.isArray(err.response.data.detail)) {
          msg = err.response.data.detail.map((e: any) => e.msg || JSON.stringify(e)).join(', ');
        }
      } else if (err?.message) {
        msg = err.message;
      }
      setEditError(msg);
      toast.error(msg);
    } finally {
      setSavingEdit(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const submitNewUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    setFormSuccess(null);
    if (!form.email.trim() || !form.password.trim()) {
      setFormError('Email et mot de passe sont requis');
      return;
    }
    setCreating(true);
    try {
      const payload = {
        email: form.email.trim(),
        password: form.password,
        is_superuser: form.role === 'admin',
        first_name: form.firstName.trim() || null,
        last_name: form.lastName.trim() || null,
      };
      const { data } = await api.post<UserWithApiKey>('/fapi/auth/register', payload);
      toast.success('Utilisateur créé');
      setUsers(prev => (prev ? [data, ...prev] : [data]));
      if (data.api_key) {
        setApiKeyModal({ email: data.email, apiKey: data.api_key });
      }
      setForm({ firstName: '', lastName: '', email: '', password: '', role: 'client' });
      setShowModal(false);
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.message || 'Erreur création utilisateur';
      setFormError(msg);
      toast.error(msg);
    } finally {
      setCreating(false);
    }
  };

  const impersonateMutation = useMutation({
    mutationFn: async (userId: number) => {
      const token = getToken();
      const response = await fetch(`${getApiBase()}/admin/impersonation/${userId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ 
          duration_minutes: 60,
          reason: 'Support utilisateur'
        }),
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Échec de l\'impersonation');
      }
      
      return response.json() as Promise<ImpersonationResponse>;
    },
    onSuccess: (data) => {
      // Sauvegarder ancien token admin
      const currentToken = getToken();
      if (currentToken) {
        localStorage.setItem('admin_token_backup', currentToken);
      }
      
      // Sauvegarder ancien user admin
      const currentUser = localStorage.getItem('user');
      if (currentUser) {
        localStorage.setItem('admin_user_backup', currentUser);
      }
      
      // Remplacer par token impersonation (utiliser 'access_token' comme dans getToken())
      localStorage.setItem('access_token', data.access_token);
      
      // Mettre à jour user object avec l'utilisateur impersonaté
      localStorage.setItem('user', JSON.stringify(data.user));
      
      // Sauvegarder infos impersonation pour le banner
      localStorage.setItem('impersonation_active', 'true');
      localStorage.setItem('impersonation_target_email', data.user.email);
      
      toast.success(`Connecté en tant que ${data.user.email}`);
      
      // Attendre que localStorage soit bien écrit avant de recharger
      setTimeout(() => {
        window.location.replace('/');
      }, 100);
    },
    onError: (error: Error) => {
      toast.error(error.message);
      setImpersonatingUserId(null);
    },
  });

  const handleImpersonate = (userId: number) => {
    if (window.confirm('Voulez-vous vraiment vous connecter en tant que cet utilisateur ?')) {
      setImpersonatingUserId(userId);
      impersonateMutation.mutate(userId);
    }
  };

  const totalUsers = users?.length || 0;

  return (
    <div className="relative overflow-hidden min-h-screen">
      {/* Background gradient */}
      <div aria-hidden className="absolute inset-0 -z-30 bg-gradient-to-b from-emerald-50 via-white to-white dark:from-zinc-950 dark:via-zinc-900 dark:to-zinc-900" />
      <div aria-hidden className="absolute inset-x-0 top-0 -z-40 h-[320px] bg-[radial-gradient(circle_at_top,_rgba(16,185,129,0.25),_transparent_68%)]" />
      <div aria-hidden className="absolute inset-0 -z-20 bg-[radial-gradient(circle_at_15%_18%,rgba(236,72,153,0.14),transparent_60%),radial-gradient(circle_at_85%_18%,rgba(168,85,247,0.17),transparent_55%)] dark:bg-[radial-gradient(circle_at_12%_18%,rgba(236,72,153,0.12),transparent_55%),radial-gradient(circle_at_88%_20%,rgba(168,85,247,0.15),transparent_50%)]" />

      {/* Contenu principal */}
      <div className="relative mx-auto max-w-[1600px] px-6 py-6">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin')}
              className="inline-flex items-center gap-2 rounded-full border border-zinc-200/70 dark:border-zinc-800 bg-white/80 dark:bg-zinc-900/70 px-4 py-2 text-sm font-medium text-zinc-700 dark:text-zinc-300 shadow-sm transition hover:bg-zinc-50 dark:hover:bg-zinc-800"
              aria-label="Retour au dashboard admin"
            >
              <ArrowLeft className="w-4 h-4" />
              Retour
            </button>
            <div>
              <h1 className="text-2xl font-bold text-zinc-900 dark:text-white flex items-center gap-3">
                <Users className="w-7 h-7 text-emerald-600 dark:text-emerald-400" aria-hidden="true" />
                Gestion des utilisateurs
              </h1>
              <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">
                {totalUsers} utilisateur{totalUsers > 1 ? 's' : ''} au total
              </p>
            </div>
          </div>
          <button
            onClick={() => {
              setFormError(null);
              setFormSuccess(null);
              setForm({ firstName: '', lastName: '', email: '', password: '', role: 'client' });
              setCreating(false);
              setShowModal(true);
            }}
            className="inline-flex items-center gap-2 rounded-full bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-emerald-500/40 transition hover:bg-emerald-600"
          >
            <Plus className="w-4 h-4" />
            Ajouter un utilisateur
          </button>
        </div>

        {/* Contenu */}
        {loading && <div className="text-center py-8 text-zinc-500"><Loader2 className="w-8 h-8 animate-spin mx-auto" /></div>}
        {error && <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-700 dark:bg-red-950/40 dark:border-red-900/60 dark:text-red-200">{error}</div>}
        {(!loading && !error) && (
          <div className="rounded-3xl border border-emerald-100/70 bg-white/90 p-8 shadow-xl shadow-emerald-500/10 dark:border-emerald-900/50 dark:bg-zinc-950/90">
            {tableMessage && <div className="mb-3 text-sm text-zinc-600 dark:text-zinc-300">{tableMessage}</div>}
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-emerald-50/50 border-b border-emerald-100/70 dark:bg-zinc-900/50 dark:border-emerald-900/40">
                  <tr>
                    <th className="px-3 py-3 text-left text-xs font-medium text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">ID</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">Prénom</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">Nom</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">Email</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">Admin</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">Quota agents</th>
                    <th className="px-3 py-3 text-right text-xs font-medium text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users?.map(u => (
                    <tr key={u.id} className="border-b border-zinc-100 dark:border-zinc-800 hover:bg-zinc-50 dark:hover:bg-zinc-900/30">
                      <td className="px-3 py-3 text-sm text-zinc-900 dark:text-zinc-100">{u.id}</td>
                      <td className="px-3 py-3 text-sm text-zinc-900 dark:text-zinc-100">{u.first_name || ''}</td>
                      <td className="px-3 py-3 text-sm text-zinc-900 dark:text-zinc-100">{u.last_name || ''}</td>
                      <td className="px-3 py-3 text-sm text-zinc-900 dark:text-zinc-100">{u.email}</td>
                      <td className="px-3 py-3 text-sm">
                        {u.is_superuser ? (
                          <span className="inline-flex items-center gap-1.5 rounded-full bg-red-100 dark:bg-red-900/30 px-2 py-0.5 text-xs font-medium text-red-700 dark:text-red-400">
                            <Shield className="w-3 h-3" />
                            Oui
                          </span>
                        ) : (
                          <span className="text-zinc-500">Non</span>
                        )}
                      </td>
                      <td className="px-3 py-3 text-sm text-zinc-900 dark:text-zinc-100">
                        {u.max_active_agents === null ? '∞' : u.max_active_agents}
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => openEditModal(u)}
                            className="p-2 rounded-full hover:bg-zinc-100 text-zinc-500 hover:text-zinc-700 transition-colors dark:hover:bg-zinc-700 dark:text-zinc-400 dark:hover:text-white"
                            title="Modifier"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => deleteUser(u.id)}
                            disabled={deletingId === u.id}
                            className="p-2 rounded-full hover:bg-red-100 text-zinc-500 hover:text-red-600 transition-colors disabled:opacity-50 dark:hover:bg-red-900/50 dark:text-zinc-400 dark:hover:text-red-300"
                            title="Supprimer"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => regenerateApiKey(u)}
                            disabled={rotatingId === u.id}
                            className="p-2 rounded-full hover:bg-emerald-100 text-zinc-500 hover:text-emerald-600 transition-colors disabled:opacity-50 dark:hover:bg-emerald-900/50 dark:text-zinc-400 dark:hover:text-emerald-300"
                            title="Régénérer clé API"
                          >
                            <Key className="w-4 h-4" />
                          </button>
                          {!u.is_superuser && (
                            <button
                              onClick={() => handleImpersonate(u.id)}
                              disabled={impersonatingUserId === u.id}
                              className="p-2 rounded-full hover:bg-violet-100 text-zinc-500 hover:text-violet-600 transition-colors disabled:opacity-50 dark:hover:bg-violet-900/50 dark:text-zinc-400 dark:hover:text-violet-300"
                              title="Se connecter en tant que"
                            >
                              <LogIn className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {users && users.length === 0 && (
                    <tr>
                      <td className="px-3 py-4 text-zinc-500 text-center" colSpan={7}>Aucun utilisateur</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Modals */}
        <Modal open={showModal} onClose={() => setShowModal(false)}>
          <h2 className="text-lg font-semibold mb-4 text-zinc-900 dark:text-white">Nouvel utilisateur</h2>
          <form className="space-y-3" onSubmit={submitNewUser}>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs mb-1 text-zinc-500">Prénom</label>
                <input className="w-full border rounded px-3 py-2 bg-transparent dark:border-zinc-700" value={form.firstName} onChange={e => setForm(prev => ({ ...prev, firstName: e.target.value }))} />
              </div>
              <div>
                <label className="block text-xs mb-1 text-zinc-500">Nom</label>
                <input className="w-full border rounded px-3 py-2 bg-transparent dark:border-zinc-700" value={form.lastName} onChange={e => setForm(prev => ({ ...prev, lastName: e.target.value }))} />
              </div>
            </div>
            <div>
              <label className="block text-xs mb-1 text-zinc-500">Email</label>
              <input className="w-full border rounded px-3 py-2 bg-transparent dark:border-zinc-700" value={form.email} onChange={e => setForm(prev => ({ ...prev, email: e.target.value }))} type="email" required />
            </div>
            <div>
              <label className="block text-xs mb-1 text-zinc-500">Mot de passe</label>
              <div className="flex gap-2">
                <input className="flex-1 border rounded px-3 py-2 bg-transparent dark:border-zinc-700" value={form.password} onChange={e => setForm(prev => ({ ...prev, password: e.target.value }))} type="password" required />
                <button
                  type="button"
                  onClick={handleGeneratePassword}
                  className="px-3 py-2 rounded border border-emerald-500 text-emerald-600 dark:text-emerald-400 dark:border-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition-colors flex items-center gap-1.5"
                  title="Générer un mot de passe fort"
                >
                  <RefreshCw className="w-4 h-4" />
                  Générer
                </button>
              </div>
              <p className="mt-1 text-xs text-zinc-500">Minimum 8 caractères. Cliquez sur "Générer" pour un mot de passe fort.</p>
            </div>
            <div>
              <label className="block text-xs mb-1 text-zinc-500">Rôle</label>
              <select className="w-full border rounded px-3 py-2 bg-transparent dark:border-zinc-700" value={form.role} onChange={e => setForm(prev => ({ ...prev, role: e.target.value }))}>
                <option value="client">Client</option>
                <option value="admin">Administrateur</option>
              </select>
            </div>
            {formError && <div className="text-sm text-red-500">{formError}</div>}
            {formSuccess && <div className="text-sm text-emerald-500">{formSuccess}</div>}
            <div className="flex gap-2 justify-end">
              <button type="button" onClick={() => setShowModal(false)} className="px-3 py-2 rounded border dark:border-zinc-700">Annuler</button>
              <button type="submit" disabled={creating} className="px-3 py-2 rounded bg-emerald-600 text-white disabled:opacity-50">
                {creating ? 'Création…' : 'Créer'}
              </button>
            </div>
          </form>
        </Modal>

        <Modal open={showEditModal} onClose={() => setShowEditModal(false)}>
          <h2 className="text-lg font-semibold mb-4 text-zinc-900 dark:text-white">Modifier l'utilisateur</h2>
          <form className="space-y-3" onSubmit={submitEditUser}>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs mb-1 text-zinc-500">Prénom</label>
                <input className="w-full border rounded px-3 py-2 bg-transparent dark:border-zinc-700" value={editForm.firstName} onChange={e => setEditForm(prev => ({ ...prev, firstName: e.target.value }))} />
              </div>
              <div>
                <label className="block text-xs mb-1 text-zinc-500">Nom</label>
                <input className="w-full border rounded px-3 py-2 bg-transparent dark:border-zinc-700" value={editForm.lastName} onChange={e => setEditForm(prev => ({ ...prev, lastName: e.target.value }))} />
              </div>
            </div>
            <div>
              <label className="block text-xs mb-1 text-zinc-500">Email</label>
              <input className="w-full border rounded px-3 py-2 bg-transparent dark:border-zinc-700" value={editForm.email} onChange={e => setEditForm(prev => ({ ...prev, email: e.target.value }))} type="email" required />
            </div>
            <div>
              <label className="block text-xs mb-1 text-zinc-500">Nouveau mot de passe (optionnel)</label>
              <input className="w-full border rounded px-3 py-2 bg-transparent dark:border-zinc-700" value={editForm.password} onChange={e => setEditForm(prev => ({ ...prev, password: e.target.value }))} type="password" placeholder="Laisser vide pour conserver" />
            </div>
            <div>
              <label className="block text-xs mb-1 text-zinc-500">Rôle</label>
              <select className="w-full border rounded px-3 py-2 bg-transparent dark:border-zinc-700" value={editForm.role} onChange={e => setEditForm(prev => ({ ...prev, role: e.target.value }))}>
                <option value="client">Client</option>
                <option value="admin">Administrateur</option>
              </select>
            </div>
            <div>
              <label className="block text-xs mb-1 text-zinc-500">Quota d'agents actifs</label>
              <input className="w-full border rounded px-3 py-2 bg-transparent dark:border-zinc-700" value={editForm.maxActiveAgents} onChange={e => setEditForm(prev => ({ ...prev, maxActiveAgents: e.target.value }))} type="number" min="0" placeholder="Illimité si vide" />
              <p className="mt-1 text-xs text-zinc-500">Laissez vide pour un quota illimité</p>
            </div>
            {editError && <div className="text-sm text-red-500">{editError}</div>}
            {editSuccess && <div className="text-sm text-emerald-500">{editSuccess}</div>}
            <div className="flex gap-2 justify-end">
              <button type="button" onClick={() => setShowEditModal(false)} className="px-3 py-2 rounded border dark:border-zinc-700">Fermer</button>
              <button type="submit" disabled={savingEdit} className="px-3 py-2 rounded bg-blue-600 text-white disabled:opacity-50">
                {savingEdit ? 'Enregistrement…' : 'Enregistrer'}
              </button>
            </div>
          </form>
        </Modal>

        <Modal open={apiKeyModal !== null} onClose={() => setApiKeyModal(null)}>
          <h2 className="text-lg font-semibold mb-3 text-zinc-900 dark:text-white">Clé API générée</h2>
          {apiKeyModal ? (
            <div className="space-y-3">
              <p className="text-sm text-zinc-600 dark:text-zinc-300">
                Conservez cette clé pour l'utilisateur <strong>{apiKeyModal.email}</strong>. Elle ne sera plus affichée après fermeture.
              </p>
              <div className="rounded-lg border border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-900 px-3 py-2 font-mono text-sm break-all">
                {apiKeyModal.apiKey}
              </div>
              <div className="flex items-center gap-2">
                <button type="button" onClick={handleCopyApiKey} className="px-3 py-2 rounded bg-emerald-600 text-white text-sm disabled:opacity-50" disabled={apiKeyCopyState === 'copied'}>
                  Copier
                </button>
                {apiKeyCopyState === 'copied' && <span className="text-xs text-emerald-600">Copié !</span>}
                {apiKeyCopyState === 'error' && <span className="text-xs text-red-500">Impossible de copier</span>}
              </div>
              <div className="flex justify-end">
                <button type="button" onClick={() => setApiKeyModal(null)} className="px-3 py-2 rounded border dark:border-zinc-700">Fermer</button>
              </div>
            </div>
          ) : null}
        </Modal>
      </div>
    </div>
  );
}
